// client/src/pages/sections/groups/PromoteSgParentButton.js
// Per-MemberCard "Make SG Parent" affordance. Used in Phase 1/2 SG
// cleanup when the original SG anchor is the wrong client — the partner
// clicks this on a non-parent member to swap which client anchors the
// cohort. SG composition is unchanged.
//
// Shown only on non-parent members; the parent already anchors the
// cohort so promoting them is a no-op. The parent component
// (MemberCard) is responsible for that gate.

import { useEffect, useRef, useState } from 'react';
import { useOptimistic } from './optimisticContext';
import { emitToast, dismissToast, errorText } from './toast';
import { postResolveAction } from './resolveActionQueue';
import ClientChip from './ClientChip';
import { useSubmitGuard } from './useSubmitGuard';

export default function PromoteSgParentButton({ member, onResolved, testMode, testSessionId, keepGroupVisible }) {
  const [open, setOpen]             = useState(false);
  const [submitting] = useState(false);   // optimistic flow closes modal; no live submitting toggle
  const guard = useSubmitGuard();         // one submit at a time: a double-click or a re-click mid-wait is ignored
  const [error, setError]           = useState(null);
  const { dismissByClientCode, restoreIds, markPending, clearPending } = useOptimistic();
  // Close on backdrop click only when the press STARTED on the backdrop, so a
  // press that begins inside the modal and releases on the overlay doesn't
  // wrongly dismiss it.
  const overlayDownRef = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting]);

  function close() {
    setOpen(false);
    setError(null);
  }

  async function submit() {
    // In a multi-member group, keep the issue row visible (loading) instead
    // of dismissing it — promoting one member doesn't resolve the group.
    markPending([member.code]);
    const hidden = keepGroupVisible ? [] : dismissByClientCode(member.code);
    close();
    const progressToastId = emitToast({
      kind: 'info',
      message: 'Calculating changes based on new groups…',
      durationMs: 0,
    });
    try {
      const data = await postResolveAction({
        verb:       'promote_to_sg_parent',
        clientCode: member.code,
        payload:    {},
        testMode:   Boolean(testMode),
        sessionId:  testMode ? testSessionId : null,
      });
      if (onResolved) await onResolved(data);
    } catch (e) {
      restoreIds(hidden);
      emitToast({
        kind: 'error',
        message: `Couldn't make ${member.code} the SG parent: ${errorText(e)}. Restored.`,
      });
    } finally {
      clearPending([member.code]);
      dismissToast(progressToastId);
    }
  }

  return (
    <>
      <button
        type="button"
        className="cg-move-btn"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title={`Make ${member.code} the Signer Group parent. The group composition stays the same; only the anchor changes.`}
      >
        Make SG Parent
      </button>

      {open && (
        <div
          className="cg-resolve-overlay"
          onMouseDown={(e) => { overlayDownRef.current = (e.target === e.currentTarget); }}
          onMouseUp={(e) => {
            const onBackdrop = overlayDownRef.current && e.target === e.currentTarget;
            overlayDownRef.current = false;
            if (onBackdrop && !submitting) close();
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="cg-resolve-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cg-resolve-header">
              <h3>Make {member.code} the Signer Group parent?</h3>
              <button
                type="button"
                className="cg-resolve-close"
                disabled={submitting}
                onClick={close}
                aria-label="Close"
              >×</button>
            </div>
            <div className="cg-resolve-summary">
              <div className="cg-resolve-summary-row">
                <ClientChip code={member.code} name={member.name} />
              </div>
            </div>
            <div className="cg-resolve-confirm">
              <p className="cg-resolve-confirm-body">
                The current Signer Group parent will become a regular member, and{' '}
                <strong>{member.code}</strong> will anchor the group. Everyone
                else in the group stays — only the anchor changes.
              </p>

              {error && (
                <div className="cg-resolve-error" role="alert">
                  <strong>Couldn't promote:</strong> {error}
                </div>
              )}

              <div className="cg-resolve-actions">
                <button
                  type="button"
                  className="cg-btn cg-btn--secondary"
                  disabled={submitting}
                  onClick={close}
                >Cancel</button>
                <button
                  type="button"
                  className="cg-btn cg-btn--primary"
                  disabled={submitting}
                  onClick={() => guard(submit)}
                >
                  {submitting ? 'Working…' : 'Yes, make SG parent'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
